import { Box, TextInput, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { UserDetail } from "@dealer-center/ddc-pages-configuration-interface";
import { FIELD_GRID, FULL_WIDTH_FIELD } from "./fieldGrid";
import type { UserInfoEdits } from "./useUserInfoEdits";

export interface GeneralInformationProps {
  summary: UserDetail;
  edits: UserInfoEdits;
}

/**
 * FR-012's four fields: Title, First Name, Last Name, Company.
 *
 * **First Name, Last Name, and Company are required; Title is not** (FR-020) — and these three are
 * the only required fields anywhere on this form. Interstate renders the requirement as the text
 * "(required)" inside the accessible label, so it is not conveyed by colour alone (FR-050). Optional
 * fields carry **no** "(optional)" tag: most fields on this tab are optional, so tagging them adds
 * noise without information.
 *
 * **Every field is editable** as of 2026-08-31, superseding FR-052's "non-editable" bullet. The
 * missing write endpoint disables the header **Save** and nothing else: a user can type, and what they
 * type survives a tab switch (FR-009), but there is nowhere to commit it. Disabling the inputs
 * themselves was the stricter reading of FR-052 and it made the tab read as "you may not change this",
 * which is not true — the field is editable in the product, only the save is unavailable. `disabled`
 * inputs also cannot become dirty, which is what blocked FR-008a's real rule from ever being
 * implemented (see `HeaderActions`).
 *
 * Values come from `edits.valueOf`, which layers pending edits over the fetched record. The state
 * lives above the tab panels because this component unmounts on every tab switch — see
 * `useUserInfoEdits`.
 *
 * FR-021's email and phone validation is still Story 2's, and still deliberately absent: it belongs
 * with the save that would reject an invalid value, and warning about a value the user cannot submit
 * anywhere is noise. The `type` values are the contract's, so the field semantics are already right
 * when that validation arrives.
 *
 * **Every field needs an explicit `id`.** Without one Interstate falls back to the constant
 * `interstate-text-input`, so all ten fields on this tab share it: the `<input>` gets no `id` at all
 * and every `<label for="interstate-text-input">` points at nothing. That breaks FR-050's "every
 * field labelled" for real — a screen reader announces an unlabelled edit field — and it is invisible
 * unless a test queries by label, which is why the specs do.
 */
export const GeneralInformation = ({
  summary,
  edits
}: GeneralInformationProps) => (
  <Box component={"section"} data-testid={"general-information"}>
    <Typography tag={"h2"} variant={"h4"}>
      {Locale.getText("GENERAL_INFORMATION") as string}
    </Typography>

    {/*
     * Not a uniform 2-up grid. Title and Company span the full width, so the row that pairs two
     * fields pairs the two that belong together — First Name beside Last Name. Left uniform, the
     * grid reads as Title│First Name and Last Name│Company, splitting the name across two rows and
     * pairing each half with something unrelated.
     *
     * `1 / -1` rather than a breakpoint-conditional span: at `xs` the grid is already one column, so
     * first-to-last-line is that single column and the rule needs no breakpoint of its own.
     */}
    <Box sx={FIELD_GRID}>
      <TextInput
        data-testid={"field-title"}
        id={"myinfo-title"}
        label={Locale.getText("COMPOSER_TITLE") as string}
        name={"title"}
        sx={FULL_WIDTH_FIELD}
        value={edits.valueOf(summary, "title")}
        onChange={event => edits.setField("title", event.target.value)}
      />
      <TextInput
        required
        data-testid={"field-first-name"}
        id={"myinfo-first-name"}
        label={Locale.getText("FIRST_NAME") as string}
        name={"firstName"}
        value={edits.valueOf(summary, "firstName")}
        onChange={event => edits.setField("firstName", event.target.value)}
      />
      <TextInput
        required
        data-testid={"field-last-name"}
        id={"myinfo-last-name"}
        label={Locale.getText("LAST_NAME") as string}
        name={"lastName"}
        value={edits.valueOf(summary, "lastName")}
        onChange={event => edits.setField("lastName", event.target.value)}
      />
      <TextInput
        required
        data-testid={"field-company"}
        id={"myinfo-company"}
        label={Locale.getText("COMPANY") as string}
        name={"company"}
        sx={FULL_WIDTH_FIELD}
        value={edits.valueOf(summary, "company")}
        onChange={event => edits.setField("company", event.target.value)}
      />
    </Box>
  </Box>
);
